import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import HeroVideo from "@/assets/videos/aipvideoo.mp4";

export default function HeroSection() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const onCanPlay = () => {
      setVideoReady(true);
      vid.play().catch(() => {/* autoplay blocked, poster shows */});
    };
    vid.addEventListener("canplay", onCanPlay);
    return () => vid.removeEventListener("canplay", onCanPlay);
  }, []);

  return (
    <section id="page-hero" data-dark-section className="relative min-h-screen overflow-hidden bg-[#0A1628]">
      {/* Floating orbs */}
      <div className="absolute w-[700px] h-[700px] bg-[#00C2C2]/12 rounded-full blur-[120px] -top-[200px] -right-[200px] pointer-events-none" />
      <div className="absolute w-[400px] h-[400px] bg-[#E8007D]/8 rounded-full blur-[100px] -bottom-[100px] -left-[100px] pointer-events-none" />

      {/* Video background */}
      <div
        className={`absolute inset-0 transition-opacity duration-1000 ${videoReady ? "opacity-0" : "opacity-100"}`}
        aria-hidden="true"
      >
        <img
          src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20Kigali%20Rwanda%20skyline%20at%20golden%20hour%2C%20modern%20business%20district%20with%20lush%20green%20hills%2C%20African%20city%20with%20contemporary%20office%20buildings%20and%20vibrant%20landscape%2C%20warm%20amber%20and%20gold%20tones%2C%20professional%20photography%20with%20dramatic%20sky&width=1920&height=1080&seq=hero-aip-poster&orientation=landscape"
          alt="Kigali Rwanda"
          className="w-full h-full object-cover object-top"
        />
      </div>

      <video
        ref={videoRef}
        className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000 ${videoReady ? "opacity-100" : "opacity-0"}`}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster="https://readdy.ai/api/search-image?query=aerial%20view%20of%20Kigali%20Rwanda%20skyline%20at%20golden%20hour%2C%20modern%20business%20district%20with%20lush%20green%20hills%2C%20African%20city%20with%20contemporary%20office%20buildings%20and%20vibrant%20landscape%2C%20warm%20amber%20and%20gold%20tones%2C%20professional%20photography%20with%20dramatic%20sky&width=1920&height=1080&seq=hero-aip-poster&orientation=landscape"
        aria-label="Background video of Kigali Rwanda business district"
      >
        <source
          src={HeroVideo}
          type="video/mp4"
        />
      </video>

      <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628]/90 via-[#0A1628]/80 to-[#0A1628]/70" />

      <div className="relative z-10 min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 w-full py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left content */}
            <div className="space-y-8">
              {/* Animated text carousel */}
              <HeroCarousel />

              {/* CTAs */}
              <div className="animate-fade-up-d3 flex flex-wrap gap-4 pt-2">
                <Link
                  to="/services"
                  className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#00C2C2] text-white font-semibold text-sm hover:bg-[#00AAAA] transition-all duration-300 hover:-translate-y-0.5"
                >
                  {t("heroCta1")}
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link
                  to="/business"
                  className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/20 text-white font-semibold text-sm hover:bg-white/10 transition-all duration-300 hover:-translate-y-0.5"
                >
                  {t("heroCta2")}
                </Link>
              </div>

              {/* Tag pills */}
              <div className="animate-fade-up-d3 flex flex-wrap gap-3 pt-2">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-xs text-white/70 font-medium">
                  <div className="w-2 h-2 rounded-full bg-[#00C2C2]" />
                  {t("tagLocal")}
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-xs text-white/70 font-medium">
                  <div className="w-2 h-2 rounded-full bg-[#E8007D]" />
                  {t("tagSpeed")}
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-xs text-white/70 font-medium">
                  <div className="w-2 h-2 rounded-full bg-[#00C2C2]" />
                  {t("tagFull")}
                </div>
              </div>

              {/* Stats */}
              <div className="animate-fade-up-d3 flex gap-8 pt-4 border-t border-white/10">
                <div>
                  <div className="text-2xl font-bold text-white">200+</div>
                  <div className="text-xs text-white/40 mt-0.5">{t("heroStat1")}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">8</div>
                  <div className="text-xs text-white/40 mt-0.5">{t("heroStat2")}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">98%</div>
                  <div className="text-xs text-white/40 mt-0.5">{t("heroStat3")}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">10+</div>
                  <div className="text-xs text-white/40 mt-0.5">{t("heroStat4")}</div>
                </div>
              </div>
            </div>

            {/* Right contact card */}
            {/* <div className="hidden lg:block relative animate-float">
              <div className="relative w-full max-w-sm ml-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-[#00C2C2]/40 to-[#E8007D]/20 rounded-3xl rotate-6 opacity-30" />
                <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-[#0A1628]/80 backdrop-blur-xl p-8 space-y-5">
                  <div className="space-y-1">
                    <p className="text-[#00C2C2] text-xs font-semibold uppercase tracking-wider">{t("contactLabel")}</p>
                    <p className="text-white text-lg font-semibold">{t("contactTitle")}</p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#00C2C2]/15 flex-shrink-0">
                        <i className="ri-phone-line text-[#00C2C2]" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">{t("navPhone")}</p>
                        <p className="text-white/40 text-xs">{t("phoneLabel")}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#00C2C2]/15 flex-shrink-0">
                        <i className="ri-mail-line text-[#00C2C2]" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">{t("navEmail")}</p>
                        <p className="text-white/40 text-xs">{t("emailLabel")}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#00C2C2]/15 flex-shrink-0">
                        <i className="ri-whatsapp-line text-[#00C2C2]" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">{t("navWhatsApp")}</p>
                        <p className="text-white/40 text-xs">{t("whatsappLabel")}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#00C2C2]/15 flex-shrink-0">
                        <i className="ri-map-pin-line text-[#00C2C2]" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">{t("navLocation")}</p>
                        <p className="text-white/40 text-xs">{t("locationLabel")}</p>
                      </div>
                    </div>
                  </div>

                  <Link
                    to="/contact"
                    className="whitespace-nowrap cursor-pointer block text-center w-full py-3 rounded-full bg-[#00C2C2] text-white font-semibold text-sm hover:bg-[#00AAAA] transition-all duration-300"
                  >
                    {t("navGetInTouch")}
                  </Link>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 opacity-40">
        <span className="text-white text-xs tracking-widest uppercase">{t("heroScroll")}</span>
        <div className="w-px h-10 bg-gradient-to-b from-white to-transparent" />
      </div>
    </section>
  );
}